import { gsap, Flip, ScrollToPlugin } from "gsap/all";
import { Guides } from "./components/guides.js";
import { OldBrowserChecker } from "./components/oldBrowserChecker.js";

/* -------------------------------------------------- */
/* 🙂 General */
/* -------------------------------------------------- */

gsap.registerPlugin(Flip, ScrollToPlugin);

gsap.defaults({
  duration: 0.25,
  ease: "power1.inOut",
});

/**
 * Checks if the user has enabled reduced motion preferences
 * @returns {boolean} True if reduced motion is preferred
 */
const isReducedMotion = () => {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/**
 * Checks if the viewport width matches desktop breakpoint
 * @returns {boolean} True if viewport width is >= 1000px
 */
const isDesktop = () => {
  return window.matchMedia("(min-width: 1000px)").matches;
};

/* -------------------------------------------------- */
/* 👵 Check for old browsers */
/* -------------------------------------------------- */

new OldBrowserChecker();

/* -------------------------------------------------- */
/* 📏 Guides */
/* -------------------------------------------------- */

new Guides();

/* -------------------------------------------------- */
/* 💬 Text toggles */
/* -------------------------------------------------- */

const textToggles = document.querySelectorAll(".text-toggle");

textToggles.forEach((textToggle) => {
  const textToggleButton = textToggle.querySelector(".text-toggle__button");

  const toggleTextToggle = () => {
    if (textToggleButton.getAttribute("aria-expanded") === "true") {
      textToggleButton.setAttribute("aria-expanded", "false");
      history.replaceState(
        null,
        null,
        document.querySelector('link[rel="canonical"]').getAttribute("href")
      );
    } else {
      textToggleButton.setAttribute("aria-expanded", "true");
      history.replaceState(null, null, textToggleButton.getAttribute("href"));
    }
  };

  textToggleButton.addEventListener("click", (e) => {
    toggleTextToggle();
    e.preventDefault();
  });
});

// Check hash

if (window.location.hash) {
  const hash = window.location.hash;
  try {
    const targetTextToggleDefinition = document.querySelector(
      ".text-toggle__definition" + hash
    );
    if (targetTextToggleDefinition) {
      let targetTextToggle = targetTextToggleDefinition.closest(".text-toggle");
      targetTextToggle
        .querySelector(".text-toggle__button")
        .setAttribute("aria-expanded", "true");
    }
  } catch (e) {
    console.error(e);
  }
}

/* -------------------------------------------------- */
/* 🏠 Home menu */
/* -------------------------------------------------- */

const homeMenu = document.querySelector(".home-menu");

if (homeMenu) {
  const menuItems = homeMenu.querySelectorAll(".menu-item");

  /**
   * Toggles the expanded state of a menu item with animation
   * @param {HTMLElement} clickedItem - The menu item element that was clicked
   * @param {boolean} [shouldAnimate=true] - Whether to animate the transition
   */
  const toggleMenuItem = (clickedItem, shouldAnimate = true) => {
    let duration = 0.5;

    if (shouldAnimate === false || isReducedMotion()) {
      duration = 0;
    }

    const state = Flip.getState(menuItems);

    // Check if we're closing the currently open item
    const isClosing =
      clickedItem
        ?.querySelector(".menu-item__title")
        ?.getAttribute("aria-expanded") === "true";
    const targetItem = isClosing ? null : clickedItem;

    // Update all items at once
    menuItems.forEach((item) => {
      const isClickedItem = item === targetItem;
      const content = item.querySelector(".menu-item__content");

      if (item.scrollTop > 0) {
        item.scrollTo({ top: 0, behavior: "smooth" });
      }

      gsap.set(item, {
        flexGrow: isClickedItem ? 1 : 0,
        height: isClickedItem ? "auto" : "",
      });

      // Toggle inert on contents
      if (content) {
        content.inert = !isClickedItem;
      }

      item
        .querySelector(".menu-item__title")
        ?.setAttribute("aria-expanded", isClickedItem ? "true" : "false");
    });

    Flip.from(state, {
      duration,
      ease: "power1.inOut",
      absolute: true,
    });

    const url = targetItem
      ? `#${targetItem.id}`
      : document.querySelector('link[rel="canonical"]')?.getAttribute("href") ??
        "";
    history.replaceState(null, null, url);
  };

  // Initial state from URL hash
  const targetMenuItem =
    window.location.hash &&
    document.querySelector(`.menu-item${window.location.hash}`);
  if (targetMenuItem) toggleMenuItem(targetMenuItem, false);

  // Menu item clicks
  homeMenu.addEventListener("click", (e) => {
    const menuTitle = e.target.closest(".menu-item__title");
    if (menuTitle) {
      if (window.scrollY > 0) {
        gsap.to(window, {
          scrollTo: 0,
          duration: 0.25,
          ease: "power1.inOut",
          onComplete: () => {
            toggleMenuItem(menuTitle.closest(".menu-item"));
          },
        });
      } else {
        toggleMenuItem(menuTitle.closest(".menu-item"));
      }
    }
  });

  // Logo click
  document.querySelector(".logo")?.addEventListener("click", (e) => {
    const openMenuItem = document.querySelector(
      '.menu-item__title[aria-expanded="true"]'
    );
    if (openMenuItem) {
      e.preventDefault();
      toggleMenuItem(null);
    }
  });
}

/* -------------------------------------------------- */
/* View Transitions Handler
/* -------------------------------------------------- */

/**
 * Determines if a URL path contains an article pattern
 * @param {string} pathname - URL pathname to check
 * @returns {boolean}
 */
const isArticlePage = (pathname) => {
  return /formate|formats|themen|topics/.test(pathname) === false;
};

/**
 * Gets the depth level of a URL path
 * @param {string} pathname - URL pathname to analyze
 * @returns {number} - Number of path segments
 */
const getPathDepth = (pathname) => {
  return pathname.split("/").filter(Boolean).length;
};

/**
 * Determines navigation direction based on URL depth and navigation type
 * @param {URL} sourceUrl - Starting URL
 * @param {URL} targetUrl - Destination URL
 * @returns {string} - Direction of navigation ('forward' or 'backward')
 */
const getNavigationDirection = (sourceUrl, targetUrl) => {
  const sourceDepth = getPathDepth(sourceUrl.pathname);
  const targetDepth = getPathDepth(targetUrl.pathname);

  // Determine direction based on path depth and article status
  if (targetDepth > sourceDepth) {
    return "forward";
  } else if (targetDepth < sourceDepth) {
    return "backward";
  } else {
    // Same depth - check if moving to/from article
    if (
      !isArticlePage(sourceUrl.pathname) &&
      isArticlePage(targetUrl.pathname)
    ) {
      console.log("forward");
      return "forward";
    } else if (
      isArticlePage(sourceUrl.pathname) &&
      !isArticlePage(targetUrl.pathname)
    ) {
      console.log("backward");
      return "backward";
    } else {
      console.log("forward");
      return "forward";
    }
  }
};

/**
 * Handle transition on the page being swapped out
 */
window.addEventListener("pageswap", (event) => {
  if (event.viewTransition) {
    try {
      const overlay = document.querySelector(".overlay");
      if (overlay) {
        const sourceUrl = new URL(event.activation.from.url);
        const targetUrl = new URL(event.activation.entry.url);

        const direction = getNavigationDirection(sourceUrl, targetUrl);
        overlay.style.viewTransitionName =
          direction === "forward" ? "old-overlay" : "overlay";
      }
    } catch (error) {
      console.error("Error during page swap:", error);
    }
  }
});

/**
 * Handle transition on the new page being revealed
 */
window.addEventListener("pagereveal", (event) => {
  if (event.viewTransition) {
    try {
      const overlay = document.querySelector(".overlay");
      if (overlay) {
        const sourceUrl = new URL(navigation.activation.from.url);
        const targetUrl = new URL(navigation.activation.entry.url);

        const direction = getNavigationDirection(sourceUrl, targetUrl);
        overlay.style.viewTransitionName =
          direction === "forward" ? "overlay" : "old-overlay";
      }
    } catch (error) {
      console.error("Error during page reveal:", error);
    }
  }
});

/* -------------------------------------------------- */
/* ↩️ Intelligent back link */
/* -------------------------------------------------- */

let backLink = document.querySelector(".flying-menu-item--back");

if (backLink) {
  backLink.addEventListener("click", function (e) {
    // Get the previous page URL from browser history
    const previousPage = document.referrer;
    // Get your website's domain
    const currentDomain = window.location.hostname;

    // Check if previous page exists and is from the same domain
    if (previousPage && previousPage.includes(currentDomain)) {
      e.preventDefault(); // Prevent default link behavior
      window.history.back(); // Use browser back button
    }

    // If no previous page or different domain, let the default link behavior work
  });
}

/* -------------------------------------------------- */
/* Image text buttons */
/* -------------------------------------------------- */

let imageWraps = document.querySelectorAll(".image-wrap");

/**
 * Toggles visibility of image text with animation
 * @param {HTMLElement} textToggleButton - Button that controls the text visibility
 * @param {HTMLElement} text - Text element to toggle
 */
const toggleImageText = (textToggleButton, text) => {
  if (!textToggleButton || !text) return;

  let isOpen = textToggleButton.getAttribute("aria-expanded") === "true";

  if (isOpen) {
    textToggleButton.setAttribute("aria-expanded", "false");
    gsap.to(text, { autoAlpha: 0, duration: 0.25, ease: "power1.inOut" });
  } else {
    textToggleButton.setAttribute("aria-expanded", "true");
    gsap.to(text, { autoAlpha: 1, duration: 0.25, ease: "power1.inOut" });
  }
};

imageWraps.forEach((imageWrap) => {
  let textToggleButton = imageWrap.querySelector(".image-wrap__text-toggle");

  if (!textToggleButton) return;

  let text = imageWrap.querySelector(".image-wrap__text");

  textToggleButton.addEventListener("click", () => {
    toggleImageText(textToggleButton, text);
  });
});
